import { observer } from 'mobx-react-lite'
import { Box, Button, Dropdown, EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { useSavedRepliesContext } from 'pages/settings/pages/savedReplies/context/savedRepliesContext'
import { SavedRepliesActions } from 'pages/settings/pages/savedReplies/ui/SavedRepliesPageContent/SavedRepliesListContent/SavedRepliesActions/SavedRepliesActions'

import styles from './styles.module.scss'

export const SavedReplyActions = observer(() => {
  const {
    newSavedReplyStore,
    selectedSavedReply,
    leftContentElement,
    handleFavoriteSavedReplies,
    handleDuplicateSavedReplies,
    handleChangeVisibilitySavedReplies,
    canManageAllSavedReplies,
    canManageSavedReplies,
    savedRepliesPermissionError,
    savedReplyVisibilityIcon,
    savedReplyVisibilityText,
    entitiesStore: {
      usersStore: { user },
    },
  } = useSavedRepliesContext()

  const {
    isPublic,
    setPublic,
    onCancel,
    onCreate,
    disabled,
    loading,
    isEdit,
    hasChanges,
    isSharedReply,
  } = newSavedReplyStore

  const onChange = (item: IDropdownItem) => {
    const isPublic = item.id === 'everyone'

    if (isEdit && selectedSavedReply) {
      handleChangeVisibilitySavedReplies(selectedSavedReply, isPublic)
      return
    }

    setPublic(isPublic)
  }

  if (isSharedReply && selectedSavedReply && !canManageAllSavedReplies) {
    return (
      <Box display='flex' gap={'8px'} paddingTop={'28px'}>
        <Button
          text={selectedSavedReply.is_favorite ? 'Unfavorite' : 'Favorite'}
          typeBtn={'contained'}
          icon={selectedSavedReply.is_favorite ? 'starFilled1' : 'starLine'}
          contained={'secondary'}
          size={'medium'}
          onClick={() => handleFavoriteSavedReplies(selectedSavedReply)}
        />
        <Button
          text={'Duplicate'}
          typeBtn={'contained'}
          contained={'secondary'}
          disabled={!canManageSavedReplies}
          tooltipProps={{
            label: savedRepliesPermissionError,
            placement: 'top',
          }}
          size={'medium'}
          onClick={() => handleDuplicateSavedReplies(selectedSavedReply)}
        />
      </Box>
    )
  }

  return (
    <div className={styles.actions}>
      <Box display='flex' gap={'8px'}>
        <Dropdown
          ariaLabel={'SavedReplyActions'}
          triggerComponent={(open) => (
            <Button
              text={isPublic ? 'All members' : savedReplyVisibilityText}
              active={open}
              contained={'secondary'}
              iconRight={open ? 'chevronUp' : 'chevronDown'}
              disabled={!canManageSavedReplies}
              tooltipProps={{
                label: savedRepliesPermissionError,
                placement: 'top',
              }}
              size={'medium'}
              customIcon={user && !isPublic ? savedReplyVisibilityIcon : undefined}
              icon={isPublic ? 'users' : undefined}
            />
          )}
          placement={'bottom-start'}
          items={[
            {
              id: 'personal',
              label: savedReplyVisibilityText,
              customIconL: user ? savedReplyVisibilityIcon : undefined,
              activeValue: !isPublic,
              variant: EnumDropdownItemVariant.CheckedRight,
              wrapLabel: 'Visibility',
            },
            {
              id: 'everyone',
              label: 'All members',
              variant: EnumDropdownItemVariant.CheckedRight,
              iconL: 'users',
              activeValue: isPublic,
            },
          ]}
          onChange={onChange}
          disabled={!canManageSavedReplies}
          tooltipProps={{
            label: savedRepliesPermissionError,
            placement: 'top',
          }}
        />
        {isEdit && selectedSavedReply && (
          <SavedRepliesActions
            savedReply={selectedSavedReply}
            tooltipParentElement={leftContentElement}
            sizeTriggerButton='medium'
            collapsed
            excludeItemsIds={['visibility']}
          />
        )}
      </Box>
      <div className={styles.right}>
        <Button
          text={'Cancel'}
          typeBtn={'contained'}
          disabled={!hasChanges}
          contained={'secondary'}
          size={'medium'}
          onClick={onCancel}
        />
        <Button
          text={isEdit ? 'Save changes' : 'Create'}
          typeBtn={'contained'}
          contained={'primary'}
          size={'medium'}
          disabled={
            disabled ||
            loading ||
            !hasChanges ||
            (isSharedReply && !canManageAllSavedReplies) ||
            !canManageSavedReplies
          }
          tooltipProps={{
            label: savedRepliesPermissionError,
            placement: 'top',
          }}
          onClick={onCreate}
        />
      </div>
    </div>
  )
})
